import React, { useEffect, useState } from "react";
import { api, API } from "@/lib/api";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import { toast } from "sonner";
import { Link2, Trash2, Check, Copy, Pencil } from "lucide-react";

export const goUrl = (slug) => `${API}/go/${slug}`;

export const QrLinksPanel = ({ form, selectedSlug, onSelect }) => {
  const [links, setLinks] = useState([]);
  const [label, setLabel] = useState("");
  const [editing, setEditing] = useState(null);
  const [editUrl, setEditUrl] = useState("");

  const load = () => api.get("/admin/qr-links").then((r) => setLinks(r.data.links)).catch(() => {});
  useEffect(() => { load(); }, []);

  const create = async () => {
    if (!form.target_url) return toast.error("Add meg a cél URL-t");
    try {
      const r = await api.post("/admin/qr-links", {
        target_url: form.target_url, label,
        utm_source: form.utm_source, utm_medium: form.utm_medium, utm_campaign: form.utm_campaign,
      });
      toast.success(`Állandó link kész: /go/${r.data.slug}`);
      setLabel("");
      onSelect(r.data.slug);
      load();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Nem sikerült létrehozni");
    }
  };

  const saveEdit = async (slug) => {
    try {
      await api.patch(`/admin/qr-links/${slug}`, { target_url: editUrl });
      toast.success("Cél frissítve – a kinyomtatott QR továbbra is működik");
      setEditing(null);
      load();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Nem sikerült menteni");
    }
  };

  const remove = async (slug) => {
    await api.delete(`/admin/qr-links/${slug}`);
    if (selectedSlug === slug) onSelect(null);
    load();
  };

  const copy = (slug) => { navigator.clipboard.writeText(goUrl(slug)); toast.success("Link másolva"); };

  return (
    <div className="rounded-2xl bg-white border border-[#2C221E]/10 p-6 space-y-4" data-testid="qr-links-panel">
      <div>
        <p className="section-label">Állandó QR linkek</p>
        <p className="text-sm text-[#5C524A] mt-1">A QR egy fix rövid linkre mutat (<span className="font-mono-brand text-[11px]">/api/go/KÓD</span>), a célt később bármikor átírhatod – a kinyomtatott kártya mindig jó marad.</p>
      </div>
      <div className="flex flex-col sm:flex-row gap-2">
        <Input placeholder="Címke (pl. Piac szórólap)" value={label} onChange={(e) => setLabel(e.target.value)} data-testid="qr-link-label-input" />
        <Button onClick={create} data-testid="qr-link-create-button" className="bg-[#4A6B53] hover:bg-[#3B5843] text-white rounded-full shrink-0">
          <Link2 className="h-4 w-4 mr-2" /> Állandó link létrehozása
        </Button>
      </div>
      <div className="space-y-2" data-testid="qr-links-list">
        {links.length === 0 && <p className="text-xs text-[#8C8075]">Még nincs állandó link.</p>}
        {links.map((l) => {
          const active = selectedSlug === l.slug;
          return (
            <div key={l.slug} className={`rounded-xl border p-3 text-sm ${active ? "border-[#4A6B53] bg-[#EAF0EC]" : "border-[#2C221E]/10"}`} data-testid={`qr-link-item-${l.slug}`}>
              <div className="flex items-center justify-between gap-2">
                <button onClick={() => onSelect(active ? null : l.slug)} data-testid={`qr-link-select-${l.slug}`} className="flex items-center gap-2 text-left font-semibold text-[#201B18]">
                  {active && <Check className="h-4 w-4 text-[#4A6B53]" />}
                  <span className="font-mono-brand text-xs">/go/{l.slug}</span>
                  {l.label && <span className="text-[#5C524A] font-normal">· {l.label}</span>}
                </button>
                <div className="flex items-center gap-2 text-[#8C8075]">
                  <span className="text-xs" data-testid={`qr-link-scans-${l.slug}`}>{l.scans} szkennelés</span>
                  <button onClick={() => copy(l.slug)} aria-label="Másolás" data-testid={`qr-link-copy-${l.slug}`}><Copy className="h-4 w-4" /></button>
                  <button onClick={() => { setEditing(l.slug); setEditUrl(l.target_url); }} aria-label="Szerkesztés" data-testid={`qr-link-edit-${l.slug}`}><Pencil className="h-4 w-4" /></button>
                  <button onClick={() => remove(l.slug)} aria-label="Törlés" data-testid={`qr-link-delete-${l.slug}`} className="hover:text-red-600"><Trash2 className="h-4 w-4" /></button>
                </div>
              </div>
              {editing === l.slug ? (
                <div className="flex gap-2 mt-2">
                  <Input value={editUrl} onChange={(e) => setEditUrl(e.target.value)} data-testid={`qr-link-edit-input-${l.slug}`} className="h-8 text-xs" />
                  <Button size="sm" onClick={() => saveEdit(l.slug)} data-testid={`qr-link-save-${l.slug}`} className="h-8 bg-[#2C221E] text-white">Mentés</Button>
                  <Button size="sm" variant="ghost" onClick={() => setEditing(null)} className="h-8">Mégse</Button>
                </div>
              ) : (
                <p className="text-xs text-[#8C8075] mt-1 break-all">→ {l.target_url}</p>
              )}
            </div>
          );
        })}
      </div>
    </div>
  );
};
